import { z } from 'zod'
import { UlidSchema } from './common'
import { RecordSchema } from './records'

/** Physical box identifier: "A".."ZZ" (the 676-box A→ZZ namespace). */
export const BoxIdSchema = z
  .string()
  .regex(/^[A-Z]{1,2}$/, 'box id "A".."ZZ"')
  .openapi('BoxId', { example: 'A' })

/**
 * A physical storage box and its current occupancy. `isFull` is DERIVED on read
 * from `occupancy >= capacity` rather than trusting a stored flag, so it can
 * never drift from the authoritative (atomically-incremented) occupancy count.
 */
export const BoxSchema = z
  .object({
    boxId: BoxIdSchema,
    capacity: z.number().int().positive(),
    occupancy: z.number().int().min(0),
    isFull: z.boolean(),
  })
  .openapi('Box')

export const ListBoxesResponseSchema = z
  .object({
    items: z.array(BoxSchema),
    allocatorState: z.object({
      currentBoxId: BoxIdSchema,
      nextSlotIndex: z.number().int().positive(),
    }),
  })
  .openapi('ListBoxesResponse')

/** A box plus the records it holds, ordered by slot index. */
export const BoxDetailResponseSchema = z
  .object({
    boxId: BoxIdSchema,
    capacity: z.number().int().positive(),
    occupancy: z.number().int().min(0),
    items: z.array(RecordSchema),
  })
  .openapi('BoxDetailResponse')

/**
 * Manually re-slot a record (admin). `toSlotIndex` is optional: omit it to drop
 * the record into the lowest free slot of `toBoxId`; supply it to target an
 * exact (currently-free, in-range) slot.
 */
export const MoveRecordRequestSchema = z
  .object({
    recordId: UlidSchema,
    toBoxId: BoxIdSchema,
    toSlotIndex: z.number().int().positive().optional(),
    /**
     * The move is rejected with ERR_BOX_HAS_SAME_CATNO when the target box
     * already holds a record with the same catalogue number (a wrong-copy-
     * shipped hazard when conditions differ). Staff who judged it harmless
     * (e.g. both copies new) resubmit with this flag to proceed anyway.
     */
    acceptSameCatno: z.boolean().optional(),
  })
  .strict()
  .openapi('MoveRecordRequest')

export type BoxId = z.infer<typeof BoxIdSchema>
export type Box = z.infer<typeof BoxSchema>
export type ListBoxesResponse = z.infer<typeof ListBoxesResponseSchema>
export type BoxDetailResponse = z.infer<typeof BoxDetailResponseSchema>
export type MoveRecordRequest = z.infer<typeof MoveRecordRequestSchema>
